import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'
import type { EnpsConfigListItem, SearchEnpsConfigsInput } from '../types'

/**
 * @fileoverview eNPS Dashboard Store
 *
 * Zustand store for eNPS list page with search, pagination, and actions
 */

export interface EnpsDashboardStore {
	// State
	enpsConfigs: EnpsConfigListItem[]
	isLoading: boolean
	error: string | null
	searchQuery: string
	selectedConfig: EnpsConfigListItem | null
	pagination: {
		page: number
		perPage: number
		totalCount: number
		totalPages: number
	}

	// Actions
	initialize: (organizationId: string) => Promise<void>
	search: (params: Partial<SearchEnpsConfigsInput>) => Promise<void>
	setSearchQuery: (query: string) => void
	setSelectedConfig: (config: EnpsConfigListItem | null) => void
	deleteSelectedConfig: () => Promise<void>
	duplicateSelectedConfig: () => Promise<void>
	setPage: (page: number) => void
}

export const createEnpsDashboardStore = () => {
	return create<EnpsDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				enpsConfigs: [],
				isLoading: false,
				error: null,
				searchQuery: '',
				selectedConfig: null,
				pagination: {
					page: 1,
					perPage: 20,
					totalCount: 0,
					totalPages: 0,
				},

				// Initialize - load initial data
				initialize: async (organizationId: string) => {
					set({ isLoading: true, error: null })

					try {
						const result = await trpcClient.client.enps.search.query({
							organizationId,
							page: 1,
							perPage: 20,
						})

						set({
							enpsConfigs: result.data,
							pagination: result.pagination,
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to initialize eNPS dashboard:', error)
						set({
							error: 'Failed to load eNPS configurations',
							isLoading: false,
						})
					}
				},

				// Search with filters
				search: async (params: Partial<SearchEnpsConfigsInput>) => {
					set({ isLoading: true, error: null })

					try {
						const result = await trpcClient.client.enps.search.query({
							organizationId: params.organizationId as string,
							search: params.search || get().searchQuery,
							page: params.page || get().pagination.page,
							perPage: params.perPage || get().pagination.perPage,
							status: params.status,
							cadence: params.cadence,
							sortBy: params.sortBy,
							order: params.order,
						})

						set({
							enpsConfigs: result.data,
							pagination: result.pagination,
							isLoading: false,
						})
					} catch (error) {
						console.error('Failed to search eNPS configs:', error)
						set({
							error: 'Failed to search eNPS configurations',
							isLoading: false,
						})
					}
				},

				// Set search query
				setSearchQuery: (query: string) => {
					set({ searchQuery: query })
				},

				// Set selected config
				setSelectedConfig: (config: EnpsConfigListItem | null) => {
					set({ selectedConfig: config })
				},

				// Delete selected config
				deleteSelectedConfig: async () => {
					const { selectedConfig } = get()
					if (!selectedConfig) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.client.enps.delete.mutate({
							id: selectedConfig.id,
							organizationId: selectedConfig.organizationId,
						})

						// Refresh list
						await get().search({ organizationId: selectedConfig.organizationId })

						set({ selectedConfig: null, isLoading: false })
					} catch (error) {
						console.error('Failed to delete eNPS config:', error)
						set({
							error: 'Failed to delete eNPS configuration',
							isLoading: false,
						})
						throw error
					}
				},

				// Duplicate selected config
				duplicateSelectedConfig: async () => {
					const { selectedConfig } = get()
					if (!selectedConfig) return

					set({ isLoading: true, error: null })

					try {
						await trpcClient.client.enps.create.mutate({
							organizationId: selectedConfig.organizationId,
							name: `${selectedConfig.name} (Copy)`,
							description: selectedConfig.description || undefined,
							cadence: selectedConfig.cadence,
							populationId: selectedConfig.populationId || undefined,
							questionText: selectedConfig.questionText,
							followUpQuestionText: selectedConfig.followUpQuestionText,
						})

						// Refresh list
						await get().search({ organizationId: selectedConfig.organizationId })

						set({ selectedConfig: null, isLoading: false })
					} catch (error) {
						console.error('Failed to duplicate eNPS config:', error)
						set({
							error: 'Failed to duplicate eNPS configuration',
							isLoading: false,
						})
						throw error
					}
				},

				// Set page
				setPage: (page: number) => {
					set((state) => ({
						pagination: { ...state.pagination, page },
					}))
				},
			}),
			{ name: 'enps-dashboard-store' },
		),
	)
}
